// Developer: Ankit Gupta
/**
 * The feedback button in the bottom corner of every screen and the drawer it opens (25 Oct 2026;
 * moved off the top bar the same day, at the owner's request).
 *
 * A drawer, not a page, so nobody leaves what they were doing to tell us about it. The drawer
 * records where it was opened (the part of the product, the page, and in Orion the application's
 * name, never what the conversation says) so the sender only writes what they think. A picture can
 * be attached or pasted. What was sent, and what became of it, is on My feedback (/feedback).
 *
 * PulsePrompt is the quarterly "how likely are you to recommend DataPilot" question: asked of people
 * who have had DataPilot for a while, a minute into a visit, once a quarter whether answered or not.
 */
import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Drawer, Input, Radio, Rate, Space, Tooltip, Typography, Upload, message } from 'antd';
import { CloseOutlined, PictureOutlined, SmileOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage, useT } from '@/i18n';
import { areaOf, FACES, feedbackApi, KINDS, type Kind } from '@/pages/Feedback/api';
import type { Thread } from '@/pages/Chat/api';
import '@/pages/Feedback/feedback.css';

const { Text } = Typography;

const SHOT_MAX = 3 * 1024 * 1024;

function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

// What the button must never cover: Orion's message box with its Send button, which runs along the
// foot of the screen and grows with what is typed and attached.
const KEEP_CLEAR = '.chat-foot, .chat-composer';
const GAP = 12;

/** How far from the foot of the screen the button sits: its usual place, or just above whatever
 *  in KEEP_CLEAR reaches under its corner (on a phone, a narrow window, or Orion beside a drawer). */
function useLift(button: React.RefObject<HTMLButtonElement | null>): number | null {
  const pathname = useLocation().pathname;
  const [lift, setLift] = useState<number | null>(null);
  useEffect(() => {
    let observed: Element | null = null;
    const ro = new ResizeObserver(() => measure());
    function measure() {
      const btn = button.current;
      const box = Array.from(document.querySelectorAll(KEEP_CLEAR)).find((el) => (el as HTMLElement).offsetParent !== null) ?? null;
      if (box !== observed) {
        if (observed) ro.unobserve(observed);
        if (box) ro.observe(box);
        observed = box;
      }
      if (!btn || !box) { setLift(null); return; }
      const b = box.getBoundingClientRect();
      const width = btn.offsetWidth + 20;
      // the button's corner: the right in English, the left in Arabic
      const rtl = getComputedStyle(btn).direction === 'rtl';
      const overlaps = rtl ? b.left < width : b.right > window.innerWidth - width;
      const reaches = b.bottom > window.innerHeight - btn.offsetHeight - 20;
      setLift(overlaps && reaches ? Math.max(20, window.innerHeight - b.top + GAP) : null);
    }
    measure();
    // the box appears after the page loads its conversation, and the window can change size
    const id = window.setInterval(measure, 800);
    window.addEventListener('resize', measure);
    return () => { window.clearInterval(id); window.removeEventListener('resize', measure); ro.disconnect(); };
  }, [button, pathname]);
  return lift;
}

export function FeedbackButton() {
  const { t } = useT('feedback');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const lift = useLift(ref);
  return (
    <>
      <Tooltip title={t('button.tip')} placement="left">
        <button ref={ref} type="button" className="dp-fb-fab" aria-label={t('button.aria')} onClick={() => setOpen(true)}
          style={lift !== null ? { insetBlockEnd: lift } : undefined}>
          <SmileOutlined />
          <span className="dp-fb-fab-label">{t('button.label')}</span>
        </button>
      </Tooltip>
      <FeedbackDrawer open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function FeedbackDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useT('feedback');
  const language = useLanguage();
  const location = useLocation();
  const qc = useQueryClient();
  const [kind, setKind] = useState<Kind>('PRAISE');
  const [rating, setRating] = useState<number | null>(null);
  const [body, setBody] = useState('');
  const [contact, setContact] = useState(true);
  const [shot, setShot] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const area = areaOf(location.pathname);
  // in an Orion conversation, the application's name from the conversation already on screen
  const threadId = /^\/orion\/(\d+)/.exec(location.pathname)?.[1];
  const application = threadId ? qc.getQueryData<Thread>(['chat-thread', Number(threadId)])?.bot_name ?? null : null;

  useEffect(() => {
    if (open) setSent(false);
  }, [open]);

  const attach = async (file: Blob) => {
    if (!/^image\/(png|jpeg|webp)$/.test(file.type)) { void message.warning(t('drawer.shotType')); return; }
    if (file.size > SHOT_MAX) { void message.warning(t('drawer.shotSize')); return; }
    setShot(await readAsDataUrl(file));
  };

  const send = useMutation({
    mutationFn: () => feedbackApi.send({
      kind, rating: kind === 'PRAISE' ? rating : null, area, page: location.pathname, application, body: body.trim(), language, contact_ok: contact, screenshot: shot,
    }),
    onSuccess: () => {
      setSent(true);
      setBody(''); setRating(null); setShot(null); setKind('PRAISE');
      void qc.invalidateQueries({ queryKey: ['feedback-mine'] });
    },
    onError: () => void message.error(t('drawer.failed')),
  });

  return (
    <Drawer open={open} onClose={onClose} title={t('drawer.title')} width={420} destroyOnClose={false}>
      {sent ? (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert type="success" showIcon message={t('drawer.thanks')} description={t('drawer.thanksSub')} />
          <Space wrap>
            <Button onClick={() => setSent(false)}>{t('drawer.another')}</Button>
            <Link to="/feedback" onClick={onClose}><Button type="link">{t('drawer.seeMine')}</Button></Link>
          </Space>
        </Space>
      ) : (
        <Space direction="vertical" size={18} style={{ width: '100%' }}
          onPaste={(e) => {
            const file = Array.from(e.clipboardData.items).find((i) => i.type.startsWith('image/'))?.getAsFile();
            if (file) { e.preventDefault(); void attach(file); }
          }}
        >
          <div>
            <Text strong>{t('drawer.kindLabel')}</Text>
            <Radio.Group value={kind} onChange={(e) => setKind(e.target.value)} optionType="button" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {KINDS.map((k) => <Radio.Button key={k} value={k}>{t(`kind.${k}`)}</Radio.Button>)}
            </Radio.Group>
          </div>
          {kind === 'PRAISE' && (
            <div>
              <Text strong>{t('drawer.ratingLabel')}</Text>
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }} role="radiogroup" aria-label={t('drawer.ratingLabel')}>
                {FACES.map((face, i) => (
                  <Tooltip key={face} title={t('rating.tip', { label: t(`rating.${i + 1}`), count: i + 1 })}>
                    <button
                      type="button" role="radio" aria-checked={rating === i + 1} aria-label={t(`rating.${i + 1}`)}
                      className={`dp-face${rating === i + 1 ? ' is-on' : ''}`}
                      onClick={() => setRating(rating === i + 1 ? null : i + 1)}
                    >{face}</button>
                  </Tooltip>
                ))}
              </div>
              {/* each face is a number of stars, which is what the averages are made of */}
              <div className="dp-face-stars">
                <Rate disabled value={rating ?? 0} style={{ fontSize: 14 }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {rating ? t('rating.stars', { count: rating, label: t(`rating.${rating}`) }) : t('rating.pick')}
                </Text>
              </div>
            </div>
          )}
          <div>
            <Text strong>{t(`drawer.bodyLabel.${kind}`)}</Text>
            <Input.TextArea
              value={body} onChange={(e) => setBody(e.target.value)} rows={6} maxLength={5000} showCount
              placeholder={t(`drawer.placeholder.${kind}`)} style={{ marginTop: 8 }}
            />
          </div>
          <div>
            {shot ? (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img src={shot} alt={t('drawer.shotAlt')} style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 8, border: '1px solid var(--dp-line)' }} />
                <Button size="small" shape="circle" icon={<CloseOutlined />} aria-label={t('drawer.shotRemove')}
                  onClick={() => setShot(null)} style={{ position: 'absolute', top: 6, insetInlineEnd: 6 }} />
              </div>
            ) : (
              <Upload accept="image/png,image/jpeg,image/webp" showUploadList={false} beforeUpload={(f) => { void attach(f); return false; }}>
                <Button icon={<PictureOutlined />}>{t('drawer.shotAdd')}</Button>
              </Upload>
            )}
            <div><Text type="secondary" style={{ fontSize: 12 }}>{t('drawer.shotHint')}</Text></div>
          </div>
          <Checkbox checked={contact} onChange={(e) => setContact(e.target.checked)}>{t('drawer.contact')}</Checkbox>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('drawer.where', { area: t(`area.${area}`) })}{application ? ` · ${application}` : ''}
          </Text>
          <Button type="primary" block loading={send.isPending} disabled={body.trim().length < 3} onClick={() => send.mutate()}>
            {t('drawer.send')}
          </Button>
        </Space>
      )}
    </Drawer>
  );
}

/** The quarterly question, at the foot of the screen. */
export function PulsePrompt() {
  const { t } = useT('feedback');
  const qc = useQueryClient();
  const [ready, setReady] = useState(false);
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [done, setDone] = useState(false);
  // not the moment someone arrives: a minute into the visit
  useEffect(() => { const id = window.setTimeout(() => setReady(true), 60_000); return () => window.clearTimeout(id); }, []);
  const state = useQuery({ queryKey: ['feedback-pulse'], queryFn: feedbackApi.pulse, enabled: ready, staleTime: Infinity, retry: false });
  const answer = useMutation({
    mutationFn: (s: number | null) => feedbackApi.answerPulse(s, comment.trim() || undefined),
    onSuccess: (_r, s) => {
      if (s === null) void qc.setQueryData(['feedback-pulse'], { ask: false, quarter: state.data?.quarter });
      else setDone(true);
    },
  });
  if (!ready || !state.data?.ask) return null;
  return (
    <div className="dp-pulse" role="dialog" aria-label={t('pulse.question')}>
      {done ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Text>{t('pulse.thanks')}</Text>
          <Button size="small" onClick={() => qc.setQueryData(['feedback-pulse'], { ask: false, quarter: state.data?.quarter })}>{t('pulse.close')}</Button>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <Text strong>{t('pulse.question')}</Text>
            <Button type="text" size="small" icon={<CloseOutlined />} aria-label={t('pulse.notNow')} onClick={() => answer.mutate(null)} />
          </div>
          <div className="dp-pulse-scale" role="radiogroup" aria-label={t('pulse.question')}>
            {Array.from({ length: 11 }, (_, n) => (
              <button key={n} type="button" role="radio" aria-checked={score === n} className={`dp-pulse-n${score === n ? ' is-on' : ''}`} onClick={() => setScore(n)}>{n}</button>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>{t('pulse.low')}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{t('pulse.high')}</Text>
          </div>
          {score !== null && (
            <>
              <Input.TextArea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} maxLength={2000} placeholder={t('pulse.why')} style={{ marginTop: 8 }} />
              <Space style={{ marginTop: 8 }}>
                <Button type="primary" size="small" loading={answer.isPending} onClick={() => answer.mutate(score)}>{t('pulse.send')}</Button>
                <Button size="small" onClick={() => answer.mutate(null)}>{t('pulse.notNow')}</Button>
              </Space>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default FeedbackButton;
